<template>
  <PTransparentLoader :loading="loading">
    <div v-if="refund">
      <div class="d-flex align-items-center mb-3">
        <CButton color="dark"
                 variant="outline"
                 class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center mr-2"
                 size="sm"
                 @click="goBack">
          <CIcon name="cipArrowLeft"/>
        </CButton>
        <h1 class="main-header mb-0 mr-2">
          <translate translate-context="accounting.refund">Refund</translate>
        </h1>
        <div class="mt-1" style="font-size: 16px">{{ refund.id }}</div>
      </div>

      <CCard class="mb-3">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.refund">Order information</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <div class="payout-cell payout-cell--highlight mb-2">
                <div class="label">
                  <translate translate-context="accounting.refund">Order Item ID</translate>
                </div>
                <div class="value">{{ refund.order_item_id || '-' }}</div>
              </div>
            </CCol>
            <CCol md="6">
              <div class="payout-cell payout-cell--highlight mb-2">
                <div class="label">
                  <translate translate-context="accounting.refund">Event</translate>
                </div>
                <div class="value">{{ refund.pica_service_name || 'N/A' }}</div>
              </div>
            </CCol>
            <CCol md="6">
              <div class="payout-cell">
                <div class="label">
                  <translate translate-context="accounting.refund">Original Order</translate>
                </div>
                <router-link
                  v-if="refund.order_id"
                  :to="getOrderRoute(refund.order_id)"
                >
                  {{ refund.order_number }}
                </router-link>
                <span v-else>-</span>
              </div>
            </CCol>

          </CRow>
        </CCardBody>
      </CCard>

      <!-- User Info -->
      <UserInfo
        v-if="refund.user_id"
        :user="refund.user_info"
        :user-id="refund.user_id"
        :current-source-id="refund.id"
        :scope="scope"
      />

      <CCard class="mb-3">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipDoc" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.refund">Refund details</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <CIcon name="cipUser" size="custom-size" :width="18" class="mr-2" style="vertical-align: sub;"/>
                  <translate translate-context="accounting.refund">Created by</translate>
                </div>
                {{ refund.created_by_email || '-' }}
              </div>
            </CCol>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <CIcon name="cipCalendar" size="custom-size" :width="18" class="mr-2"
                         style="vertical-align: sub;"/>
                  <translate translate-context="accounting.refund">Created at</translate>
                </div>
                {{ refund.created_at|formatDateTime('L LT') }}
              </div>
            </CCol>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Type</translate>
                </div>
                <CBadge v-if="refund.refund_type" color="primary" class="badge-outline">
                  {{ refund.refund_type.name }}
                </CBadge>
                <span v-else>-</span>
              </div>
            </CCol>
            <CCol md="12">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Reason</translate>
                </div>
                <PAlert color="warning">
                  {{ refund.reason || '-' }}
                </PAlert>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Amounts -->
      <CCard class="mb-3">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipMoney" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.refund">Refund amounts</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Amount Refunded</translate>
                </div>
                <div class="text-danger font-weight-medium">
                  -{{ formatAmount(refund.amount) }}
                </div>
              </div>
            </CCol>
            <CCol md="6">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Net Amount</translate>
                </div>
                <div class="font-weight-medium">{{ formatAmount(refund.amount_net) }}</div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Credit Note -->
      <CCard class="mb-3" v-if="refund.credit_note_number">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipPencil" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="accounting.refund">Credit note</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Credit Note Number</translate>
                </div>
                {{ refund.credit_note_number || '-' }}
              </div>
            </CCol>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Created at</translate>
                </div>
                {{ refund.credit_note_created_at|formatDateTime('L LT') }}
              </div>
            </CCol>
            <CCol md="4">
              <div class="payout-cell">
                <div class="mb-1">
                  <translate translate-context="accounting.refund">Sent at</translate>
                </div>
                {{ refund.credit_note_sent_at|formatDateTime('L LT') }}
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Ledger Entries -->
      <LedgerEntriesTable :entries="refund.ledger_entries"/>
    </div>
  </PTransparentLoader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import LedgerEntriesTable from '../components/LedgerEntriesTable.vue'
import UserInfo from '../components/UserInfo.vue'
import {getRoutePrefix, getDetailRouteParams, TRANSACTION_TYPES} from '../constants'
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'RefundDetail',
  components: {
    PAlert,
    PTransparentLoader,
    LedgerEntriesTable,
    UserInfo
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    // Set scope first, then load data
    await this.setScope(this.scope)
    await this.loadRefund()
  },
  watch: {
    '$route.params.id'() {
      this.loadRefund()
    }
  },
  computed: {
    ...mapGetters('accounting/refund', ['refund']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getRoutePrefix(this.scopeType)
    },

    stakeholderBreakdown() {
      if (!this.refund || !this.refund.ledger_entries) return []

      const totals = {}
      for (const entry of this.refund.ledger_entries) {
        const type = entry.stakeholder ? entry.stakeholder.stakeholder_type : 'unknown'
        if (!totals[type]) {
          totals[type] = 0
        }
        totals[type] += parseFloat(entry.amount) || 0
      }

      const absTotal = Math.abs(this.totalRefundAmount) || 1

      return Object.entries(totals).map(([stakeholder_type, amount]) => ({
        stakeholder_type,
        amount,
        percentage: Math.round((Math.abs(amount) / absTotal) * 100)
      }))
    },

    totalRefundAmount() {
      if (!this.refund || !this.refund.ledger_entries) return 0
      return this.refund.ledger_entries.reduce(
        (sum, entry) => sum + (parseFloat(entry.amount) || 0),
        0
      )
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/refund', ['fetchRefund']),

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({name: this.listRouteName, params})
    },

    async loadRefund() {
      this.loading = true
      try {
        await this.fetchRefund(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    getOrderRoute(orderId) {
      return getDetailRouteParams(TRANSACTION_TYPES.ORDER, orderId, this.scope)
    },

    getStakeholderLabel(type) {
      return STAKEHOLDER_TYPE_LABELS[type] || type || '-'
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    },
  }
}
</script>